import { TranslationDto } from '../translation.dto'

/**
 * FR-03 — loại Milestone.
 * Khớp enum `challenge_milestone_type` của DB (`ChallengeMilestone.type`).
 */
export const MILESTONE_TYPE = {
  COMPLETE_PROFILE: 'COMPLETE_PROFILE',
  VISIT_MALL: 'VISIT_MALL',
  CHECK_IN_EVENT: 'CHECK_IN_EVENT',
  MALL_SHOPPING: 'MALL_SHOPPING',
  FOOD_ORDERING: 'FOOD_ORDERING',
  ONLINE_SHOPPING: 'ONLINE_SHOPPING'
} as const

export type MilestoneType = typeof MILESTONE_TYPE[keyof typeof MILESTONE_TYPE]

/**
 * Milestone dùng gian hàng Online (`Tenant`) hay Offline (`OfflineTenant`).
 * DB tách hai bảng liên kết nên FE phải gửi đúng mảng id tương ứng.
 *
 * ✅ ĐÃ CHỐT (C36 đóng): `FOOD_ORDERING` dùng `offlineTenantIds` — cùng nhóm với
 * `MALL_SHOPPING`.
 */
export function isOnlineTenantMilestone(type?: MilestoneType): boolean {
  return type === MILESTONE_TYPE.ONLINE_SHOPPING
}

export function isOfflineTenantMilestone(type?: MilestoneType): boolean {
  return type === MILESTONE_TYPE.MALL_SHOPPING || type === MILESTONE_TYPE.FOOD_ORDERING
}

export function isPurchaseMilestone(type?: MilestoneType): boolean {
  return isOnlineTenantMilestone(type) || isOfflineTenantMilestone(type)
}

/**
 * Milestone có giới hạn theo Mall không. Online shopping thì **không**: mọi Tenant online đều
 * nằm chung 1 Mall mặc định, mua online không gắn với Mall nào ⇒ luôn áp dụng mọi Mall.
 */
export function isMallScopedMilestone(type?: MilestoneType): boolean {
  return !isOnlineTenantMilestone(type)
}

/**
 * Bảng `ChallengeMilestone` + `ChallengeMilestoneTranslation` + 3 bảng liên kết.
 *
 * ✅ API #10 khớp **13/13** field nghiệp vụ: `id`, `position`, `type`, `imageId`,
 * `logoId`, `eventId`, `minimumExpense`, `firstPurchaseOnly`, `applyAllMalls`,
 * `mallIds`, `applyAllTenants`, `tenantIds`, `offlineTenantIds`.
 * `name`/`description` đến từ `translations[]` (xem `stamp-collection.mapper.ts`).
 */
export class ChallengeMilestoneDto {
  public id?: string
  /**
   * `ChallengeMilestone.position` (> 0, unique theo challenge) — quyết định sequence
   * khi `sequentialMilestones = true` (FR-02-BR-03) và mã hiển thị MS-00x.
   */
  public position?: number
  public type?: MilestoneType
  public name?: TranslationDto
  public description?: TranslationDto
  public imageId?: string
  public imageUrl?: string
  public logoId?: string
  public logoUrl?: string

  /** FR-03-BR-05 — Event khác "Took place". ✅ API #10 trả `eventId`. */
  public eventId?: string
  /**
   * ⚠️ BE-MISSING: API #10 chỉ trả `eventId`, không trả tên Event.
   * `AdminChallengeService.listEventOptions()` phải join để hiển thị.
   */
  public eventName?: TranslationDto

  /** Mall shopping / Food ordering / Online shopping (FR-03-BR-06/07/08). */
  public minimumExpense?: number
  /** `ChallengeMilestone.firstPurchaseOnly` — chỉ Food ordering + Online shopping. */
  public firstPurchaseOnly?: boolean

  /**
   * Milestone có QR **tĩnh** khả dụng hay không — cùng ngữ nghĩa `qrAvailable` của API #21:
   * chỉ `true` với `VISIT_MALL` / `CHECK_IN_EVENT` và khi payload QR thật sự tồn tại trong
   * DB. BE không trả mã QR thô ở detail, phải gọi API #15.
   *
   * Cần ở đây vì API #21 **không có dòng** cho Milestone chưa phát tem — thiếu nó thì
   * Challenge vừa tạo không xem được QR để in mã. Xem
   * `AdminStampHistoryService.mergeSummaryWithMilestones()`.
   *
   * ⚠️ Chỉ có trong **response**; `toMilestoneBody()` không gửi field này lên.
   */
  public qrAvailable?: boolean

  /**
   * `applyAllMalls` NOT NULL — true nghĩa là áp dụng mọi Mall của Challenge,
   * khi đó `mallIds` bỏ trống. Visit mall (FR-03-BR-04) luôn chọn đúng 1 Mall.
   */
  public applyAllMalls?: boolean
  /** `ChallengeMilestoneMall` — Mall áp dụng riêng cho mốc này. */
  public mallIds?: string[]

  /** `applyAllTenants` NOT NULL — true nghĩa là mọi gian hàng trong Mall đã chọn. */
  public applyAllTenants?: boolean
  /** `ChallengeMilestoneTenant` — gian hàng Online (FK `Tenant`). */
  public tenantIds?: string[]
  /** `ChallengeMilestoneOfflineTenant` — gian hàng Offline (FK `OfflineTenant`). */
  public offlineTenantIds?: string[]

  public static create(data?: Partial<ChallengeMilestoneDto>): ChallengeMilestoneDto {
    const instance = new ChallengeMilestoneDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

export function hasImplicitMallScope(type?: MilestoneType): boolean {
  return type === MILESTONE_TYPE.COMPLETE_PROFILE || type === MILESTONE_TYPE.CHECK_IN_EVENT
}

export function createMilestoneScope(type?: MilestoneType): Partial<ChallengeMilestoneDto> {
  return {
    applyAllMalls: type !== MILESTONE_TYPE.VISIT_MALL,
    mallIds: [],
    applyAllTenants: isPurchaseMilestone(type),
    tenantIds: [],
    offlineTenantIds: []
  }
}

/** FR-03-BR-05 — option Event để chọn cho Milestone Check in event (Event khác "Took place"). */
export class MilestoneEventOptionDto {
  public id?: string
  public name?: TranslationDto
  public mallId?: string

  public static create(data?: Partial<MilestoneEventOptionDto>): MilestoneEventOptionDto {
    const instance = new MilestoneEventOptionDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/**
 * API #15 `GET /challenges/:challengeId/milestones/:milestoneId/qr-code`
 *
 * QR **tĩnh** cho `VISIT_MALL` và `CHECK_IN_EVENT` (FR-06-BR-05).
 * Trước đây FE đọc `ChallengeMilestoneDto.qrCode` / `StampHistorySummaryDto.qrCode`
 * trực tiếp từ response detail/summary — nay bắt buộc gọi API riêng.
 *
 * ✅ Swagger 2026-08-25: response thật là
 * `{challengeId, milestoneId, milestoneType, qrPayload}` — **không có `qrCode`**, giải quyết
 * dứt điểm thắc mắc trước đây. `qrPayload` là giá trị duy nhất đem render QR.
 */
export class MilestoneQrDto {
  public challengeId?: string
  public milestoneId?: string
  /** Kèm sẵn để double-check đúng loại QR tĩnh (VISIT_MALL/CHECK_IN_EVENT). */
  public milestoneType?: MilestoneType
  /** Payload opaque do BE mã hoá — dùng trực tiếp để render QR. */
  public qrPayload?: string

  public static create(data?: Partial<MilestoneQrDto>): MilestoneQrDto {
    const instance = new MilestoneQrDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/**
 * API #16 `POST /challenges/:challengeId/milestones/:milestoneId/mall-shopping-qr`
 *
 * FR-03-BR-06 — QR **động** của Mall shopping: hết hạn ~60s, dùng 1 lần, server ký.
 * BE ghi rõ *"After manual receipt verification"* ⇒ chỉ sinh sau khi admin xác minh
 * hoá đơn thủ công. Không có bảng DB tương ứng — sinh runtime, không lưu.
 */
export class MilestoneDynamicQrDto {
  /** ⚠️ BE trả tên `qrToken`; service map sang `qrCode` để giữ nguyên component. */
  public qrCode?: string
  /** ISO Date string — thời điểm QR hết hạn. ✅ khớp BE. */
  public expiresAt?: string
  /**
   * ⚠️ BE-MISSING: API #16 **không trả** field này.
   * `AdminStampHistoryService` tự tính `(expiresAt - now) / 1000` để chạy countdown.
   */
  public expiresInSeconds?: number

  public static create(data?: Partial<MilestoneDynamicQrDto>): MilestoneDynamicQrDto {
    const instance = new MilestoneDynamicQrDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}
